import { useMemo } from 'react';

import { ListItem, ListItemProps } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import { Constants, ROIMetricDisplayType } from '@arrived/common';
import { AccountOfferingBalance } from '@arrived/models';

import { PrivacyContextAwareMoneyValue } from '../common';
import {
  CellBodyTypography,
  EquityMetric,
  FirstDividendDate,
  OfferingInfoCell,
  RentalIncomePercentMetric,
} from '../common/cells';

const StyledPortfolioOfferingListItem = styled(ListItem)`
  &.MuiListItem-root {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(2.5)};
    justify-content: flex-start;
    padding: 0;

    &:hover {
      background-color: ${({ theme }) => theme.palette.neutrals.milk};
    }
  }

  .roi-display {
    text-align: right;
    margin-left: auto;
  }
`;

export interface PortfolioOfferingListItemProps extends ListItemProps {
  accountOfferingBalance: AccountOfferingBalance;
  displayType: ROIMetricDisplayType;
}

export const PortfolioOfferingListItem = ({
  accountOfferingBalance,
  children,
  className,
  displayType,
  ...rest
}: PortfolioOfferingListItemProps) => {
  const { imgUrl, name, marketName, totalValue, appreciation, rentalIncome, id } = accountOfferingBalance;

  const monetaryValueDisplay = useMemo(() => {
    switch (displayType) {
      case ROIMetricDisplayType.EQUITY:
        return <PrivacyContextAwareMoneyValue length={5} value={totalValue} />;
      case ROIMetricDisplayType.RENTAL_INCOME:
        return rentalIncome.latestDividend === null ? (
          <FirstDividendDate offeringId={id} />
        ) : (
          <PrivacyContextAwareMoneyValue length={5} value={rentalIncome.totalDividendsPaid} />
        );
    }
  }, [displayType, totalValue, rentalIncome.totalDividendsPaid]);

  return (
    <StyledPortfolioOfferingListItem className={clsx('list-item', className)} {...rest}>
      <OfferingInfoCell imgSize={56} imgUrl={imgUrl} market={marketName} name={name} />
      <div className="roi-display">
        <CellBodyTypography fontWeight="bold">{monetaryValueDisplay}</CellBodyTypography>
        {displayType === ROIMetricDisplayType.EQUITY ? (
          <EquityMetric appreciationInfo={Constants.sharePriceUpdatedOfferings.includes(id) ? appreciation : null} />
        ) : (
          <RentalIncomePercentMetric
            value={rentalIncome.annualizedDividendPercent}
            latestDividend={rentalIncome.latestDividend}
          />
        )}
      </div>
      {children}
    </StyledPortfolioOfferingListItem>
  );
};
